class Pagination extends HTMLElement {
  constructor() {
    super();

    const paginateContent = document.getElementById("Paginate-Content");
    if (!paginateContent) return;

    this.updateUrl = true; // Whether to update the browser URL during pagination
    this.sectionId = paginateContent.dataset.section;
    this.contentListWrapper = paginateContent.querySelector(".content-list");
    if (!this.sectionId || !this.contentListWrapper) return;
    this.bindListener();
    this.bindPopState();
  }

  bindListener() {
    this.querySelectorAll(".pagination-item").forEach((item) => {
      item.addEventListener("click", this.handleEvent.bind(this));
    });
  }

  /* ===== Listen for browser back/forward navigation ===== */
  bindPopState() {
    if (!this.updateUrl) return; // Only the numbered pagination should react to popstate

    this.boundPopStateHandler = () => {
      const queryString = window.location.search.slice(1);
      if (queryString) this.fetchContent(queryString, false);
    };
    window.addEventListener("popstate", this.boundPopStateHandler);
  }

  disconnectedCallback() {
    if (this.boundPopStateHandler)
      window.removeEventListener("popstate", this.boundPopStateHandler);
  }

  handleEvent(event = null) {
    if (event) event.preventDefault();
    const url = event.currentTarget.href;
    if (!url) return;
    const queryString = url.split("?")[1];
    if (queryString) this.fetchContent(queryString, true);
  }

  /* ===== Fetch Newly loaded content ===== */
  fetchContent(queryString = null, pushState = true) {
    if (!queryString) return;
    this.startLoading();

    webvista
      .fetchHtml(
        `${window.location.pathname}?section_id=${this.sectionId}&${queryString}`,
      )
      .then((html) => {
        if (html) this.renderHtml(html);
        // Save the browser URL and add a new history entry (not replace)
        if (this.updateUrl && pushState)
          window.history.pushState(
            {},
            "",
            `${window.location.pathname}?${queryString}`,
          );
      })
      .catch((error) => {
        webvista.popToast(
          window["accessibilityStrings"]["unknownError"],
          "error",
        );
      })
      .finally(() => {
        this.endLoading();
      });
  }

  /* ===== Render content ===== */
  renderHtml(html) {
    const sourceContentDom = this.getSourceContent(html);
    if (!sourceContentDom) return;

    this.renderContent(sourceContentDom);
    this.renderPagination(html);

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(this.contentListWrapper); 
    }

    if (typeof window.initDynamicPrices === "function") {
      window.initDynamicPrices();
    }

    if (window.yotpoWidgetsContainer) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    window.FlairApp.init();

    if (typeof window.initRatingPlaceholders === "function") {
      window.initRatingPlaceholders();
    }

    initProductSliderCollection()

    webvista.initLazyImages(); 
    webvista.initTooltips();
  }

  /* ===== Get content from the source ===== */
  getSourceContent(html) {
    if (!html) return;
    return html.querySelector("#Paginate-Content .content-list");
  }

  /* ===== Render the list ===== */
  renderContent(sourceContentDom) {
    this.contentListWrapper.innerHTML = sourceContentDom.innerHTML;
    requestAnimationFrame(() => {
      webvista.scrollElementToHeaderBottom(this.contentListWrapper, 32);
    });
  }

  /* ===== Render pagination ===== */
  renderPagination(html) {
    // Update pagination
    const sourcePaginationContainer = html.querySelector(".pagination-container");
    const targetPaginationContainer = this.closest(".pagination-container");
    if (sourcePaginationContainer && targetPaginationContainer)
      targetPaginationContainer.innerHTML = sourcePaginationContainer.innerHTML;
  }

  startLoading() {
    this.contentListWrapper.classList.add("loading");
  }

  endLoading() {
    this.contentListWrapper.classList.remove("loading");
  }
}

customElements.define("pagination-page", Pagination);

/* ===== Load more pagination ====== */
class PaginationMore extends Pagination {
  constructor() {
    super();
    this.updateUrl = false;
  }

  // Add event listeners
  bindListener() {
    this.button = this.querySelector(".pagination-more-button");
    this.boundClickHandler = this.handleEvent.bind(this);
    if (this.button)
      this.button.addEventListener("click", this.boundClickHandler);
  }

  renderContent(sourceContentDom) {
    const fragment = document.createDocumentFragment();
    Array.from(sourceContentDom.childNodes).forEach((node) => {
      fragment.appendChild(node);
    });
    this.contentListWrapper.appendChild(fragment);
  }

  startLoading() {
    super.startLoading();
    this.button.classList.add("loading");
    this.button.setAttribute("aria-disabled", true);
  }

  endLoading() {
    super.endLoading();
    this.button.classList.remove("loading");
    this.button.removeAttribute("aria-disabled");
  }
}

customElements.define("pagination-more", PaginationMore);

class PaginationScrollMore extends Pagination {
  constructor() {
    super();
    this.updateUrl = false;
  }

  bindListener() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleEvent();
            observer.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px 200px 0px" },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  handleEvent(event = null) {
    const url = this.getAttribute("data-url");
    if (!url) return;
    const queryString = url.split("?")[1];
    if (queryString) this.fetchContent(queryString);
  }

  renderContent(sourceContentDom) {
    const fragment = document.createDocumentFragment();
    Array.from(sourceContentDom.childNodes).forEach((node) => {
      fragment.appendChild(node);
    });
    this.contentListWrapper.appendChild(fragment);
  }

  startLoading() {
    super.startLoading();
  }

  endLoading() {
    super.endLoading();
  }
}

customElements.define("pagination-scroll-more", PaginationScrollMore);